<template>
  <v-container fluid class="worker-role-form mt-3 mx-1 centered-tabs" v-show="showComponent">
    <v-form ref="form">
      <v-row>
        <v-col cols="12">
          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{
              edit ? $t('book/admin:documentation-rule.edit.titles.edit') : $t('book/admin:documentation-rule.edit.titles.add')
            }}
          </h2>

          <v-card
              elevation="0"
              flat
              tile
          >
            <v-card-text>
              <v-row justify="center">
                <v-col cols="12" md="5">
                  <!-- FacilityDocumentationType -->
                  <v-select
                      v-model="documentationRule.name"
                      outlined
                      :label="$t('book/admin:documentation-rule.common.docu-type')"
                      :menu-props="{ bottom: true, offsetY: true }"
                      :items="restDocumentTypes"
                      item-text="translatedName"
                      item-value="name"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      :no-data-text="$t('book/admin:documentation-rule.edit.no-types-found')"
                      tabindex="1"
                  />

                  <!-- FacilityType description -->
                  <v-text-field
                      outlined
                      clearable
                      v-model="documentationRule.priority"
                      :label="$t('book/admin:documentation-rule.common.priority')"
                      :rules="[ruleRequired($t('utils:rules.required')), ruleNumberFromTo(1, 3, $t('utils:rules.number-from-to', {to: '1', from: '3'}))]"
                      :hint="$t('utils:hints.number-from-to', {to: '1', from: '3'})"
                      persistent-hint
                      tabindex="2"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card
              elevation="0"
              flat
              tile
          >
            <v-card-text class="pa-5 pb-4">
              <BaseButton
                  color="primary"
                  :btn-text="$t('buttons:save')"
                  @clickedButton="saveDocumentationRule()"
              />

              <TwoButtonDialog
                  v-if="edit"
                  ref="deleteButton"
                  :btn-text="$t('buttons:delete')"
                  color="error"
                  :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/admin:documentation-rule.edit.dialog-part-1')})"
                  :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/admin:documentation-rule.edit.dialog-part-2')})"
                  :button1-text="$t('buttons:cancel')"
                  :button2-text="$t('buttons:delete')"
                  @clickedButton1="$refs.deleteButton.showDialog = false"
                  @clickedButton2="deleteDocumentationRule()"
              />

              <DirtyConfirmButtonDialog
                  ref="dirtyDialog"
                  :btn-text="$t('buttons:back')"
                  color="grey"
                  :dialog-title="$t('dialog:dirty-save-btn-title')"
                  :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                  :button1-text="$t('dialog:dirty-save-btn-1-text')"
                  :button2-text="$t('dialog:dirty-save-btn-2-text')"
                  @clickedButton="$router.go(-1)"
                  :dataToWatch="documentationRule"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>

import DocumentationRuleService from "@/modules/book/services/documentation-rule-service";
import InfoService from "@/modules/core/services/info-service";

import Utils from "@/modules/core/services/utils";
import DataUtils from "@/modules/core/services/data-utils";
import {ruleRequired, ruleNumberFromTo} from "@/modules/core/services/rules";

import DocumentationRule from "@/modules/book/data/documentation-rule";

import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";

export default {
  name: 'DocumentationRuleForm',
  components: {
    TwoButtonDialog,
    DirtyConfirmButtonDialog,
    BaseButton
  },
  data() {
    return {
      documentationRule: new DocumentationRule(),
      edit: false,
      isFetching: true,
      restDocumentTypes: []
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },
    showComponent() {
      if (this.edit) {
        return !this.isFetching;
      } else {
        return true
      }
    }
  },
  methods: {
    saveDocumentationRule() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      if (!this.edit) {
        DocumentationRuleService.postToAPI(this.documentationRule,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('book/admin:documentation-rule.edit.snackbar.rule')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('book/admin:documentation-rule.edit.snackbar.rule')}))
          }
        )
      } else {
        DocumentationRuleService.putToAPI(this.documentationRule,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('book/admin:documentation-rule.edit.snackbar.rule')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('book/admin:documentation-rule.edit.snackbar.rule')}))
          }
        )
      }
    },

    deleteDocumentationRule() {
      this.$refs.deleteButton.setButtonSettings(true, true, false, true);

      DocumentationRuleService.deleteFromAPI(this.documentationRule,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('book/admin:documentation-rule.edit.snackbar.rule')}));
          this.$router.go(-1)
        },
        () => {
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('book/admin:documentation-rule.edit.snackbar.rule')}))
        }
      )
    },

    translateAndSortTypes() {
      for (let j = 0; j < this.restDocumentTypes.length; j++) {
        this.restDocumentTypes[j].translatedName = this.$t('enums:facility-documentation-types.' + this.restDocumentTypes[j].name);
      }

      this.restDocumentTypes.sort((a, b) => {
        if (a.translatedName < b.translatedName) {
          return -1;
        }

        if (a.translatedName > b.translatedName) {
          return 1;
        }

        return 0;
      })
    },

    ruleRequired,
    ruleNumberFromTo
  },
  watch: {
    currentLang() {
      this.$refs.form.resetValidation();
      this.translateAndSortTypes();
    }
  },
  mounted() {
    this.$refs.dirtyDialog.startWatch();
  },
  created() {
    if (this.$route.params.id) {
      this.edit = true;

      DocumentationRuleService.readFromAPI(this.$route.params.id,
        (res) => {
          this.documentationRule = DataUtils.parseDataToSingleObject(res.data, DocumentationRule);
          if (this.documentationRule == null) {
            this.$router.go(-1);
          } else {
            this.isFetching = false;
            this.$refs.dirtyDialog.startWatch();
          }
        },
        (error) => {
          this.$router.push('/404')
          this.$logger.error("Error at getting Documentation Rule: " + error)
        }
      )
    }


    InfoService.readEnum("documentation/facility",
      (resp) => {

        // remove facilityDocumentationTypes, if they have already been added previously
        DocumentationRuleService.readAllFromAPI(
          (res) => {
            for (let i = 0; i < res.data.length; i++) {
              for (let j = resp.data.length - 1; j >= 0; j--) {
                if (res.data[i].name === resp.data[j] && this.documentationRule.name !== resp.data[j] ) {
                  resp.data.splice(j, 1);
                }
              }
            }

            for (let i = 0; i < resp.data.length; i++) {
              this.restDocumentTypes.push({name: resp.data[i]})
            }

            this.translateAndSortTypes()
            this.$refs.dirtyDialog.startWatch();
          }
        )
      },
      (error) => {
        this.$logger.error("Error at InfoService.readEnum: " + error);
        Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/admin:documentation-rule.edit.snackbar.types')}))
      }
    )
  }
}
</script>